import React, { useEffect, useState } from "react";
import supabase from "../../../utils/supabase";
import { toast } from "react-toastify";
import { getClasses } from "../../../utils/getClasses";
import DeploymentPicker, {
  defaultDeployment,
  type DeploymentSelection,
} from "./DeploymentPicker";
import type { Tier } from "../../../engine/sequenceEngine";
import { useT, useUiLang } from "../../../stores/uiLang";
import { peCurriculum } from "../../../data/peCurriculum";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * Teacher-facing modal to assign a Project Explore chapter as a NATIVE
 * (non-LMS) assignment. Distinct from the PFL `CreateAssignmentModal` — that one
 * is assessment-scoped (quiz/midterm/final/project) and is left untouched.
 *
 * The chosen chapter/tier/language are packed into the existing `assessment_id`
 * column as a composite key `pe:{chapterId}:{tier}:{lang}`, with `type` set to
 * `pe_chapter`. The student dashboard (AssignmentTable) routes these rows to
 * `/student/lesson/:assignmentId`, which unpacks the key and opens SceneReader.
 *
 * This feeds the SAME `createAssignment` edge function as the PFL flow — no
 * backend change. (The Google-Classroom sync branch in that fn is intentionally
 * NOT extended for PE; the no-LMS path never reaches it.)
 */

// Run E3 B4: the whole modal follows the UI language (EN text unchanged).
// D37: each tier shows its grade band. T1 = K-1, T2 = 2nd, T3 = 3rd, T4 = 4th-5th.
const AM_T = {
  en: {
    tiers: {
      T1: "Tier 1 (K-1)",
      T2: "Tier 2 (2nd grade)",
      T3: "Tier 3 (3rd grade)",
      T4: "Tier 4 (4th-5th grade)",
    },
    langs: { en: "English", es: "Spanish (Español)" },
    loadChaptersFail: "Failed to load Project Explore chapters.",
    classesFail: "Failed to fetch classes. Please try again.",
    noClasses: "No classes available. Please create a class first.",
    classesErr: "An unexpected error occurred while fetching classes",
    noClassesToFetch: "No classes available to fetch students from",
    noStudentsFound: "No students found in the selected classes",
    noProfiles: "No student profiles found",
    studentsErr: "An unexpected error occurred while fetching students",
    pickChapter: "Please select a chapter",
    pickPart: "Select at least one part to assign to students",
    pickClass: "Please select at least one class",
    pickStudent: "Please select at least one student",
    startReq: "Start date is required",
    startTimeReq: "Start time is required",
    dueReq: "Due date is required",
    dueTimeReq: "Due time is required",
    dueAfter: "Due date must be after start date",
    fixErrors: "Please fix the errors before submitting",
    createFail: "Failed to create assignment. Please try again.",
    assigned: "Project Explore chapter assigned successfully.",
    title: "Assign Project Explore Chapter",
    sub: "Choose a chapter, tier, and language, then assign it to your class",
    close: "Close",
    chapter: "Chapter",
    selectChapter: "Select a chapter…",
    tier: "Tier",
    language: "Language",
    assignTo: "Assign To",
    entireClass: "Entire Class",
    individuals: "Individual Students",
    selectClasses: "Select Classes",
    loadingClasses: "Loading classes…",
    noClassesAvail: "No classes available.",
    selectStudents: "Select Students",
    errLoadingStudents: "Error loading students",
    noStudentsIn: "No students found in selected classes",
    startLabel: "Start Date & Time",
    dueLabel: "Due Date & Time",
    pickStartFirst: "Please select a start date first",
    instructions: "Instructions (optional)",
    instructionsPh: "Add any specific instructions for students…",
    cancel: "Cancel",
    assigning: "Assigning…",
    assignBtn: "Assign Chapter",
  },
  es: {
    tiers: {
      T1: "Nivel 1 (K-1)",
      T2: "Nivel 2 (2.º grado)",
      T3: "Nivel 3 (3.er grado)",
      T4: "Nivel 4 (4.º-5.º grado)",
    },
    langs: { en: "Inglés (English)", es: "Español" },
    loadChaptersFail: "No se pudieron cargar los capítulos de Project Explore.",
    classesFail: "No se pudieron cargar las clases. Inténtalo otra vez.",
    noClasses: "No hay clases disponibles. Primero crea una clase.",
    classesErr: "Ocurrió un error inesperado al cargar las clases",
    noClassesToFetch: "No hay clases de donde cargar estudiantes",
    noStudentsFound: "No hay estudiantes en las clases elegidas",
    noProfiles: "No se encontraron perfiles de estudiantes",
    studentsErr: "Ocurrió un error inesperado al cargar los estudiantes",
    pickChapter: "Elige un capítulo",
    pickPart: "Elige al menos una parte para asignar a los estudiantes",
    pickClass: "Elige al menos una clase",
    pickStudent: "Elige al menos un estudiante",
    startReq: "La fecha de inicio es obligatoria",
    startTimeReq: "La hora de inicio es obligatoria",
    dueReq: "La fecha de entrega es obligatoria",
    dueTimeReq: "La hora de entrega es obligatoria",
    dueAfter: "La fecha de entrega debe ser después de la fecha de inicio",
    fixErrors: "Corrige los errores antes de enviar",
    createFail: "No se pudo crear la tarea. Inténtalo otra vez.",
    assigned: "El capítulo de Project Explore se asignó.",
    title: "Asignar un capítulo de Project Explore",
    sub: "Elige un capítulo, un nivel y un idioma, y luego asígnalo a tu clase",
    close: "Cerrar",
    chapter: "Capítulo",
    selectChapter: "Elige un capítulo…",
    tier: "Nivel",
    language: "Idioma",
    assignTo: "Asignar a",
    entireClass: "Toda la clase",
    individuals: "Estudiantes individuales",
    selectClasses: "Elige las clases",
    loadingClasses: "Cargando las clases…",
    noClassesAvail: "No hay clases disponibles.",
    selectStudents: "Elige los estudiantes",
    errLoadingStudents: "No se pudieron cargar los estudiantes",
    noStudentsIn: "No hay estudiantes en las clases elegidas",
    startLabel: "Fecha y hora de inicio",
    dueLabel: "Fecha y hora de entrega",
    pickStartFirst: "Primero elige una fecha de inicio",
    instructions: "Instrucciones (opcional)",
    instructionsPh: "Escribe instrucciones para los estudiantes, si quieres…",
    cancel: "Cancelar",
    assigning: "Asignando…",
    assignBtn: "Asignar capítulo",
  },
};
const TIER_VALUES = ["T1", "T2", "T3", "T4"] as const;


interface PeChapterOption {
  id: number;
  chapter_number: number;
  title: string;
  season_number: number;
}

interface FormErrors {
  chapter?: string;
  assignmentTarget?: string;
  deployment?: string;
  dueDate?: string;
  startDate?: string;
  submit?: string;
}

export default function CreatePeAssignmentModal({ isOpen, onClose }) {
  const t = useT(AM_T);
  const uiLang = useUiLang();
  // Spanish UI: show the approved Spanish chapter name (pe_chapters has English only).
  const chapterName = (c: PeChapterOption) =>
    (uiLang === "es" &&
      peCurriculum.find((x) => x.season === c.season_number && x.chapter === c.chapter_number)?.title_es) ||
    c.title;
  const [chapters, setChapters] = useState<PeChapterOption[]>([]);
  const [chapterId, setChapterId] = useState<string>("");
  const [tier, setTier] = useState("T1");
  const [lang, setLang] = useState("en");
  // What ships to students (spec §5). Defaults to the Daytime preset; persisted
  // as the parallel `deployment` field. Parallel to the composite key — never
  // packed into it.
  const [deployment, setDeployment] = useState<DeploymentSelection>(
    defaultDeployment("T1")
  );

  const [assignTo, setAssignTo] = useState("class");
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [assignedClass, setAssignedClass] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [instructions, setInstructions] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchClasses();
      fetchChapters();
    }
  }, [isOpen]);

  const resetForm = () => {
    setChapterId("");
    setTier("T1");
    setLang("en");
    setDeployment(defaultDeployment("T1"));
    setAssignTo("class");
    setSelectedStudents([]);
    setAssignedClass([]);
    // Default the start to "now" (local) so fresh-assignment walkthroughs can
    // pick today. min on the date input is start-of-today local (see render).
    setStartDate(dayjs().format("YYYY-MM-DD"));
    setStartTime(dayjs().format("HH:mm"));
    setDueDate("");
    setDueTime("");
    setInstructions("");
    setErrors({});
    setFetchError("");
  };

  async function fetchChapters() {
    // pe_chapters has `SELECT TO authenticated USING (true)`, so a teacher can
    // read the published chapter list directly.
    const { data, error } = await supabase
      .from("pe_chapters")
      .select("id, chapter_number, title, is_published, pe_seasons ( season_number )")
      .eq("is_published", true)
      .order("season_id", { ascending: true })
      .order("chapter_number", { ascending: true });

    if (error) {
      console.error("Error fetching PE chapters:", error);
      setFetchError(t.loadChaptersFail);
      return;
    }

    const options: PeChapterOption[] = (data || []).map((c: any) => ({
      id: c.id,
      chapter_number: c.chapter_number,
      title: c.title,
      season_number: c.pe_seasons?.season_number ?? 0,
    }));
    setChapters(options);
  }

  async function fetchClasses() {
    try {
      setIsLoading(true);
      setFetchError("");
      const { classes, error } = await getClasses();
      if (error) {
        setFetchError(t.classesFail);
        toast.error(error);
      } else if (!classes || classes.length === 0) {
        setFetchError(t.noClasses);
      } else {
        setClasses(classes);
      }
    } catch (e) {
      console.error("Unexpected error in fetchClasses:", e);
      setFetchError(t.classesErr);
    } finally {
      setIsLoading(false);
    }
  }

  async function getStudents() {
    try {
      setFetchError("");
      const classIds = classes.map((c) => c.id);
      if (classIds.length === 0) {
        setFetchError(t.noClassesToFetch);
        return;
      }
      const { data: classStudentsIds, error: classError } = await supabase
        .from("class_students")
        .select("user_id")
        .in("class_id", classIds);
      if (classError || !classStudentsIds || classStudentsIds.length === 0) {
        setFetchError(t.noStudentsFound);
        return;
      }
      const { data: users, error: userError } = await supabase
        .from("profiles")
        .select("*")
        .in("id", classStudentsIds.map((c) => c.user_id));
      if (userError || !users || users.length === 0) {
        setFetchError(t.noProfiles);
        return;
      }
      setStudents(users);
    } catch (e) {
      console.error("Unexpected error in getStudents:", e);
      setFetchError(t.studentsErr);
    }
  }

  const handleAssignToChange = async (value: string) => {
    setAssignTo(value);
    setErrors({});
    if (value === "individuals") await getStudents();
  };

  const toggleClass = (classItem: any) => {
    setAssignedClass((prev) => {
      const exists = prev.some((c) => c.id === classItem.id);
      return exists
        ? prev.filter((c) => c.id !== classItem.id)
        : [...prev, classItem];
    });
  };

  const toggleStudent = (id: string) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    if (dueDate && date && dueDate < date) {
      setDueDate("");
      setDueTime("");
    }
    if (errors.dueDate) setErrors((prev) => ({ ...prev, dueDate: undefined }));
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!chapterId) newErrors.chapter = t.pickChapter;

    // §5 empty-state submit block — an assignment must ship at least one part.
    if (deployment.assets.length === 0) {
      newErrors.deployment = t.pickPart;
    }

    if (assignTo === "class" && assignedClass.length === 0) {
      newErrors.assignmentTarget = t.pickClass;
    } else if (assignTo === "individuals" && selectedStudents.length === 0) {
      newErrors.assignmentTarget = t.pickStudent;
    }

    if (!startDate) newErrors.startDate = t.startReq;
    else if (!startTime) newErrors.startDate = t.startTimeReq;

    if (!dueDate) newErrors.dueDate = t.dueReq;
    else if (!dueTime) newErrors.dueDate = t.dueTimeReq;
    else if (startDate && startTime) {
      const startDateTime = dayjs(`${startDate}T${startTime}`);
      const dueDateTime = dayjs(`${dueDate}T${dueTime}`);
      if (dueDateTime.isSame(startDateTime) || dueDateTime.isBefore(startDateTime)) {
        newErrors.dueDate = t.dueAfter;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const selectedChapter = chapters.find((c) => String(c.id) === chapterId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!validateForm()) {
      toast.error(t.fixErrors);
      return;
    }
    if (!selectedChapter) return;

    setIsSubmitting(true);

    const startDateTime = dayjs(`${startDate}T${startTime}:00`);
    const dueDateTime = dayjs(`${dueDate}T${dueTime}:00`);

    const tierLabel = tier;
    const langLabel = lang.toUpperCase();
    // Composite content key — unpacked by /student/lesson/:assignmentId.
    const assessmentId = `pe:${selectedChapter.id}:${tier}:${lang}`;
    // Human-readable label shown in the student dashboard "Chapter" column.
    const standard = `PE · S${selectedChapter.season_number} Ch${selectedChapter.chapter_number} · ${tierLabel} · ${langLabel}`;

    const payload = {
      assessmentId,
      assessmentName: selectedChapter.title,
      standard,
      assignTo,
      type: "pe_chapter",
      assigned_class: assignTo === "class" ? assignedClass : null,
      selectedStudents: assignTo === "individuals" ? selectedStudents : [],
      scaffoldLevel: "clean",
      retakeOption: "noRetake",
      dueDate: dueDateTime.utc().toISOString(),
      startDate: startDateTime.utc().toISOString(),
      instructions: instructions.trim(),
      origin: window.location.origin,
      // Parallel to the composite key — what ships to students (spec §5).
      // The engine treats absent/null as the full CANONICAL_STEPS loop; an
      // explicit object is equivalent + auditable. The current createAssignment
      // edge fn ignores unknown body keys, so this no-ops until the
      // `assignments.deployment jsonb` column + edge-fn insert land (owned by
      // the DB-writer terminal). NEVER packed into assessment_id / targeting.
      deployment,
    };

    try {
      const { data, error } = await supabase.functions.invoke("createAssignment", {
        body: payload,
      });
      if (error) {
        console.error("Function error:", error.message);
        toast.error(t.createFail);
        setErrors({ submit: t.createFail });
      } else {
        toast.success(t.assigned);
        resetForm();
        onClose();
        return { data, error: null };
      }
    } catch (err: any) {
      console.error("Error creating PE assignment:", err);
      const msg = err?.message || t.createFail;
      toast.error(msg);
      setErrors({ submit: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-6 flex flex-col max-h-[85%]">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t.title}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t.sub}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isSubmitting}
            aria-label={t.close}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {fetchError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{fetchError}</p>
          </div>
        )}

        {/* Form */}
        <div className="overflow-y-auto flex-1 pr-1">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Chapter */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t.chapter}</label>
              <select
                className={`mt-1 block w-full p-2 border rounded-md shadow-sm ${
                  errors.chapter ? "border-red-300" : "border-gray-300"
                }`}
                value={chapterId}
                onChange={(e) => setChapterId(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">{t.selectChapter}</option>
                {chapters.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    S{c.season_number} · Ch{c.chapter_number} — {chapterName(c)}
                  </option>
                ))}
              </select>
              {errors.chapter && (
                <p className="mt-1 text-sm text-red-600">{errors.chapter}</p>
              )}
            </div>

            {/* Tier + Language */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t.tier}</label>
                <select
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  disabled={isSubmitting}
                >
                  {TIER_VALUES.map((v) => (
                    <option key={v} value={v}>
                      {t.tiers[v]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t.language}</label>
                <select
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  disabled={isSubmitting}
                >
                  {(["en", "es"] as const).map((v) => (
                    <option key={v} value={v}>
                      {t.langs[v]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Deployment picker — what ships to students (spec §5) */}
            <div>
              <DeploymentPicker
                tier={tier as Tier}
                value={deployment}
                onChange={(next) => {
                  setDeployment(next);
                  if (errors.deployment)
                    setErrors((prev) => ({ ...prev, deployment: undefined }));
                }}
                disabled={isSubmitting}
              />
              {errors.deployment && (
                <p className="mt-1 text-sm text-red-600">{errors.deployment}</p>
              )}
            </div>

            {/* Assign To */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t.assignTo}</label>
              <select
                className={`mt-1 block w-full p-2 border rounded-md shadow-sm ${
                  errors.assignmentTarget ? "border-red-300" : "border-gray-300"
                }`}
                value={assignTo}
                onChange={(e) => handleAssignToChange(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="class">{t.entireClass}</option>
                <option value="individuals">{t.individuals}</option>
              </select>
              {errors.assignmentTarget && (
                <p className="mt-1 text-sm text-red-600">{errors.assignmentTarget}</p>
              )}
            </div>

            {/* Class or Students */}
            {assignTo === "class" ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.selectClasses}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {isLoading ? (
                    <p className="text-sm text-gray-500">{t.loadingClasses}</p>
                  ) : classes.length === 0 ? (
                    <p className="text-sm text-gray-500">{t.noClassesAvail}</p>
                  ) : (
                    classes.map((cls) => (
                      <label key={cls.id} className="text-sm text-gray-700 flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                          checked={assignedClass.some((c) => c.id === cls.id)}
                          onChange={() => toggleClass(cls)}
                          disabled={isSubmitting}
                        />
                        {cls.name}
                      </label>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700">{t.selectStudents}</label>
                <div className="space-y-2 mt-2 max-h-48 overflow-y-auto border p-2 rounded-md">
                  {students.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {fetchError ? t.errLoadingStudents : t.noStudentsIn}
                    </p>
                  ) : (
                    students.map((student) => (
                      <label
                        key={student.id}
                        className="flex items-center p-1 hover:bg-gray-50 rounded"
                      >
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => toggleStudent(student.id)}
                          disabled={isSubmitting}
                        />
                        {student.fullname}
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t.startLabel}</label>
              <div className="mt-1 flex space-x-2">
                <input
                  type="date"
                  className={`w-1/2 border rounded-md shadow-sm ${
                    errors.startDate ? "border-red-300" : "border-gray-300"
                  }`}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  disabled={isSubmitting}
                  min={dayjs().format("YYYY-MM-DD")}
                  value={startDate}
                />
                <input
                  type="time"
                  className="w-1/2 border-gray-300 rounded-md shadow-sm"
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={isSubmitting}
                  value={startTime}
                />
              </div>
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t.dueLabel}</label>
              <div className="mt-1 flex space-x-2">
                <input
                  type="date"
                  className={`w-1/2 border rounded-md shadow-sm ${
                    errors.dueDate ? "border-red-300" : "border-gray-300"
                  } ${!startDate ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={isSubmitting || !startDate}
                  min={startDate || new Date().toISOString().split("T")[0]}
                  value={dueDate}
                />
                <input
                  type="time"
                  className={`w-1/2 border-gray-300 rounded-md shadow-sm ${
                    !startDate ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  onChange={(e) => setDueTime(e.target.value)}
                  disabled={isSubmitting || !startDate}
                  value={dueTime}
                />
              </div>
              {!startDate && (
                <p className="mt-1 text-sm text-gray-500">{t.pickStartFirst}</p>
              )}
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
              )}
            </div>

            {/* Instructions */}
            <div>
              <label htmlFor="pe-instructions" className="block text-sm font-medium text-gray-700">
                {t.instructions}
              </label>
              <textarea
                id="pe-instructions"
                rows={3}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm"
                placeholder={t.instructionsPh}
                disabled={isSubmitting}
                maxLength={1000}
              />
            </div>

            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-small btn-secondary"
            disabled={isSubmitting}
          >
            {t.cancel}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="btn btn-small"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? t.assigning : t.assignBtn}
          </button>
        </div>
      </div>
    </div>
  );
}
